import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminCustomersApi, adminDiscountsApi, adminGiftCardsApi } from '../api/operations';
import { adminCategoriesApi, adminProductsApi } from '../api/catalog';
import type {
  CategoryDto,
  DiscountDto,
  DiscountQueryDto,
  DiscountStatus,
  DiscountType,
  DiscountUpsertDto,
  GiftCardCreateDto,
  GiftCardQueryDto,
  GiftCardStatus,
} from '../api/types';
import {
  discountAllocationMethodValues,
  discountCombinationValues,
  discountCustomerSelectionValues,
  discountSelectionValues,
  discountStatusValues,
  discountTargetValues,
  discountTypeValues,
  giftCardStatusValues,
} from '../api/types';
import { Column, DataTable, SearchInput, SelectFilter, Toolbar } from '../components/DataTable';
import { PageHeader } from '../components/Shell';
import { ConfirmDialog, Field, FormErrors, Modal, Pagination, Spinner } from '../components/ui';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { formatDate, money, number } from '../lib/format';
import {
  discountCombinationLabels,
  discountCustomerSelectionLabels,
  discountSelectionLabels,
  discountTargetLabels,
  discountTypeLabels,
} from '../lib/labels';
import { P } from '../lib/permissions';

/* ================================ INDIRIMLER ================================ */

const discountStatusLabels: Record<DiscountStatus, string> = {
  Draft: 'Taslak',
  Scheduled: 'Zamanlanmış',
  Active: 'Aktif',
  Expired: 'Süresi doldu',
  Disabled: 'Kapalı',
};

function emptyDiscount(): DiscountUpsertDto {
  return {
    name: '',
    description: '',
    type: 'Percentage',
    target: 'LineItem',
    status: 'Draft',
    isAutomatic: false,
    codes: [],
    percentage: 10,
    allocationMethod: 'Across',
    appliesTo: 'All',
    entitledIds: [],
    excludedIds: [],
    customerSelection: 'All',
    customerIds: [],
    customerGroupIds: [],
    getEntitledIds: [],
    startsAt: new Date().toISOString(),
    combination: 'None',
    priority: 0,
    entitledShippingZoneIds: [],
  };
}

function flattenCategories(categories: CategoryDto[], prefix = ''): { id: number; name: string }[] {
  let result: { id: number; name: string }[] = [];
  for (const cat of categories) {
    const fullName = prefix ? `${prefix} > ${cat.name}` : cat.name;
    result.push({ id: cat.id, name: fullName });
    if (cat.children && cat.children.length > 0) {
      result = result.concat(flattenCategories(cat.children, fullName));
    }
  }
  return result;
}

function CategoryPicker({
  selectedIds,
  onChange,
}: {
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}) {
  const { data: tree = [] } = useQuery({
    queryKey: ['admin', 'categories', 'tree'],
    queryFn: () => adminCategoriesApi.tree(true),
  });

  const flat = flattenCategories(tree);

  const toggle = (id: number) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  return (
    <div className="space-y-1.5 rounded-lg border border-ink-200 bg-ink-50/50 p-2.5">
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold text-ink-800">Dahil Edilen Kategoriler</label>
        {selectedIds.length > 0 ? (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-[11px] text-red-600 hover:underline"
          >
            Tümünü kaldır
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto rounded bg-white p-2 border border-ink-200 text-xs">
        {selectedIds.length === 0 ? (
          <span className="text-ink-400">Henüz kategori seçilmedi. Aşağıdan seçebilirsiniz.</span>
        ) : (
          selectedIds.map((id) => {
            const cat = flat.find((c) => c.id === id);
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 rounded bg-brand-50 px-2 py-0.5 font-medium text-brand-700 border border-brand-200"
              >
                {cat?.name ?? `Kategori #${id}`}
                <button
                  type="button"
                  onClick={() => toggle(id)}
                  className="text-brand-500 hover:text-brand-900 ml-0.5 font-bold"
                >
                  ×
                </button>
              </span>
            );
          })
        )}
      </div>

      <select
        className="field text-xs bg-white"
        value=""
        onChange={(e) => {
          if (e.target.value) {
            toggle(Number(e.target.value));
          }
        }}
      >
        <option value="">+ Kategori ekle / çıkar...</option>
        {flat.map((cat) => (
          <option key={cat.id} value={cat.id}>
            {selectedIds.includes(cat.id) ? '✓ ' : ''}
            {cat.name}
          </option>
        ))}
      </select>
    </div>
  );
}

function ProductPicker({
  selectedIds,
  onChange,
}: {
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}) {
  const [term, setTerm] = useState('');

  const { data: searchResult, isFetching } = useQuery({
    queryKey: ['admin', 'products', 'picker', term],
    queryFn: () => adminProductsApi.search({ search: term, pageSize: 8 }),
    enabled: term.trim().length >= 2,
  });

  const remove = (id: number) => {
    onChange(selectedIds.filter((x) => x !== id));
  };

  const add = (id: number) => {
    if (!selectedIds.includes(id)) {
      onChange([...selectedIds, id]);
    }
  };

  return (
    <div className="space-y-1.5 rounded-lg border border-ink-200 bg-ink-50/50 p-2.5">
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold text-ink-800">Dahil Edilen Ürünler</label>
        {selectedIds.length > 0 ? (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-[11px] text-red-600 hover:underline"
          >
            Tümünü kaldır
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto rounded bg-white p-2 border border-ink-200 text-xs">
        {selectedIds.length === 0 ? (
          <span className="text-ink-400">Henüz ürün seçilmedi. Aşağıdan ürün arayarak ekleyebilirsiniz.</span>
        ) : (
          selectedIds.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1 rounded bg-brand-50 px-2 py-0.5 font-medium text-brand-700 border border-brand-200"
            >
              Ürün #{id}
              <button
                type="button"
                onClick={() => remove(id)}
                className="text-brand-500 hover:text-brand-900 ml-0.5 font-bold"
              >
                ×
              </button>
            </span>
          ))
        )}
      </div>

      <div className="relative">
        <input
          type="text"
          placeholder="Ürün adı veya kodu yazarak arayın..."
          className="field text-xs bg-white"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
        />
        {isFetching && (
          <div className="absolute right-2.5 top-2.5">
            <Spinner className="h-3.5 w-3.5 text-ink-400" />
          </div>
        )}

        {term.trim().length >= 2 && searchResult && (
          <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-48 overflow-y-auto rounded-lg border border-ink-200 bg-white p-1 shadow-lg">
            {searchResult.items.length === 0 ? (
              <p className="p-2 text-center text-xs text-ink-500">Eşleşen ürün bulunamadı.</p>
            ) : (
              searchResult.items.map((prod) => {
                const isSelected = selectedIds.includes(prod.id);
                return (
                  <button
                    key={prod.id}
                    type="button"
                    onClick={() => {
                      if (isSelected) remove(prod.id);
                      else add(prod.id);
                    }}
                    className={`flex w-full items-center justify-between gap-2 rounded px-2.5 py-1.5 text-left text-xs transition ${
                      isSelected ? 'bg-brand-50 font-medium text-brand-800' : 'hover:bg-ink-100 text-ink-800'
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0 truncate">
                      {prod.primaryImageUrl ? (
                        <img src={prod.primaryImageUrl} alt="" className="h-6 w-6 rounded object-cover" />
                      ) : (
                        <div className="h-6 w-6 rounded bg-ink-200" />
                      )}
                      <span className="truncate">{prod.title}</span>
                    </div>
                    <span className="text-[10px] text-ink-400 shrink-0">
                      {isSelected ? '✓ Seçili' : '+ Ekle'}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CustomerPicker({
  selectedIds,
  onChange,
}: {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}) {
  const [term, setTerm] = useState('');

  const { data: searchResult, isFetching } = useQuery({
    queryKey: ['admin', 'customers', 'picker', term],
    queryFn: () => adminCustomersApi.search({ search: term, pageSize: 8 }),
    enabled: term.trim().length >= 2,
  });

  const remove = (id: string) => {
    onChange(selectedIds.filter((x) => x !== id));
  };

  const add = (id: string) => {
    if (!selectedIds.includes(id)) {
      onChange([...selectedIds, id]);
    }
  };

  return (
    <div className="space-y-1.5 rounded-lg border border-ink-200 bg-ink-50/50 p-2.5">
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold text-ink-800">Seçilen Özel Müşteriler</label>
        {selectedIds.length > 0 ? (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-[11px] text-red-600 hover:underline"
          >
            Tümünü kaldır
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto rounded bg-white p-2 border border-ink-200 text-xs">
        {selectedIds.length === 0 ? (
          <span className="text-ink-400">Henüz müşteri seçilmedi. Aşağıdan isim, e-posta veya telefon ile arayarak ekleyebilirsiniz.</span>
        ) : (
          selectedIds.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1 rounded bg-brand-50 px-2 py-0.5 font-medium text-brand-700 border border-brand-200"
            >
              Müşteri #{id.slice(0, 8)}…
              <button
                type="button"
                onClick={() => remove(id)}
                className="text-brand-500 hover:text-brand-900 ml-0.5 font-bold"
              >
                ×
              </button>
            </span>
          ))
        )}
      </div>

      <div className="relative">
        <input
          type="text"
          placeholder="Müşteri adı, e-posta veya telefon yazarak arayın..."
          className="field text-xs bg-white"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
        />
        {isFetching && (
          <div className="absolute right-2.5 top-2.5">
            <Spinner className="h-3.5 w-3.5 text-ink-400" />
          </div>
        )}

        {term.trim().length >= 2 && searchResult && (
          <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-48 overflow-y-auto rounded-lg border border-ink-200 bg-white p-1 shadow-lg">
            {searchResult.items.length === 0 ? (
              <p className="p-2 text-center text-xs text-ink-500">Eşleşen müşteri bulunamadı.</p>
            ) : (
              searchResult.items.map((cust) => {
                const isSelected = selectedIds.includes(cust.id);
                return (
                  <button
                    key={cust.id}
                    type="button"
                    onClick={() => {
                      if (isSelected) remove(cust.id);
                      else add(cust.id);
                    }}
                    className={`flex w-full items-center justify-between rounded p-2 text-left text-xs transition ${
                      isSelected ? 'bg-brand-50 text-brand-800 font-semibold' : 'hover:bg-ink-50 text-ink-700'
                    }`}
                  >
                    <div>
                      <span className="font-medium text-ink-900">{cust.fullName || 'İsimsiz Müşteri'}</span>
                      <span className="ml-2 text-ink-500">{cust.email}</span>
                      {cust.phoneNumber && <span className="ml-2 text-ink-400">{cust.phoneNumber}</span>}
                    </div>
                    <span>{isSelected ? '✓ Eklendi' : '+ Ekle'}</span>
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function DiscountsPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [query, setQuery] = useState<DiscountQueryDto>({ page: 1, pageSize: 20 });
  const [editing, setEditing] = useState<{ id: number | null; form: DiscountUpsertDto } | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [generateFor, setGenerateFor] = useState<number | null>(null);
  const [generateCount, setGenerateCount] = useState(100);

  const discounts = useQuery({
    queryKey: ['admin', 'discounts', query],
    queryFn: () => adminDiscountsApi.search(query),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'discounts'] });

  const save = useMutation({
    mutationFn: () =>
      editing!.id === null
        ? adminDiscountsApi.create(editing!.form)
        : adminDiscountsApi.update(editing!.id, editing!.form),
    onSuccess: () => {
      toast.success('İndirim kaydedildi.');
      setEditing(null);
      void invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminDiscountsApi.remove(id),
    onSuccess: (message) => {
      toast.success(message ?? 'İndirim silindi.');
      setDeleteId(null);
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'İndirim silinemedi.'),
  });

  const toggleStatus = useMutation({
    mutationFn: (id: number) => adminDiscountsApi.toggleStatus(id),
    onSuccess: (updated: DiscountDto) => {
      toast.success(`İndirim durumu güncellendi: ${discountStatusLabels[updated.status]}`);
      void invalidate();
    },
    onError: (err) => toast.fromError(err, 'Durum güncellenemedi.'),
  });

  const generate = useMutation({
    mutationFn: () => adminDiscountsApi.generateCodes(generateFor!, generateCount),
    onSuccess: (codes) => {
      toast.success(`${codes.length} kod üretildi.`);
      // Kodlar yalnizca burada gorunur; personel dosya olarak indirir.
      const blob = new Blob([codes.join('\n')], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'indirim-kodlari.txt';
      link.click();
      URL.revokeObjectURL(url);
      setGenerateFor(null);
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'Kod üretilemedi.'),
  });

  const columns: Column<NonNullable<typeof discounts.data>['items'][number]>[] = [
    {
      key: 'name',
      header: 'İndirim',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink-900">{row.name}</p>
          <p className="truncate text-xs text-ink-400">
            {row.isAutomatic ? 'Otomatik' : row.codes.slice(0, 3).join(', ') || 'Kod yok'}
            {row.codes.length > 3 ? ` +${row.codes.length - 3}` : ''}
          </p>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Tür',
      cell: (row) => (
        <span className="badge bg-ink-100 text-ink-700">{discountTypeLabels[row.type]}</span>
      ),
    },
    {
      key: 'value',
      header: 'Değer',
      align: 'right',
      cell: (row) =>
        row.type === 'Percentage'
          ? `%${row.percentage ?? 0}`
          : row.type === 'FreeShipping'
            ? 'Ücretsiz kargo'
            : money(row.amount ?? 0),
    },
    {
      key: 'usage',
      header: 'Kullanım',
      align: 'right',
      hideOnMobile: true,
      cell: (row) => (
        <span className="text-ink-600">
          {number(row.usageCount)}
          {row.usageLimit ? ` / ${number(row.usageLimit)}` : ''}
        </span>
      ),
    },
    {
      key: 'period',
      header: 'Dönem',
      hideOnMobile: true,
      cell: (row) => (
        <span className="text-xs text-ink-500">
          {formatDate(row.startsAt)}
          {row.endsAt ? ` – ${formatDate(row.endsAt)}` : ' – süresiz'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Durum',
      cell: (row) => (
        <button
          type="button"
          disabled={!can(P.discounts.manage) || toggleStatus.isPending}
          onClick={() => toggleStatus.mutate(row.id)}
          title={can(P.discounts.manage) ? 'Tıklayarak durumu Aktif / Kapalı olarak değiştirin' : undefined}
          className={`badge transition hover:opacity-85 active:scale-95 cursor-pointer ${
            row.status === 'Active'
              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
              : row.status === 'Expired' || row.status === 'Disabled'
                ? 'bg-ink-100 text-ink-600 border border-ink-200'
                : 'bg-amber-50 text-amber-800 border border-amber-200'
          }`}
        >
          <span className="mr-1.5 h-1.5 w-1.5 rounded-full inline-block bg-current" />
          {discountStatusLabels[row.status]}
        </button>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (row) =>
        can(P.discounts.manage) ? (
          <div className="flex justify-end gap-1">
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={() => toggleStatus.mutate(row.id)}
              disabled={toggleStatus.isPending}
              title="Aktif/Pasif durumunu değiştir"
            >
              {row.status === 'Active' ? 'Durdur' : 'Aktifleştir'}
            </button>
            {!row.isAutomatic ? (
              <button
                type="button"
                className="btn-ghost btn-sm"
                onClick={() => setGenerateFor(row.id)}
                title="Tek kullanımlık kampanya kodları üretir"
              >
                Kod üret
              </button>
            ) : null}
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={() =>
                setEditing({
                  id: row.id,
                  form: {
                    name: row.name,
                    description: row.description,
                    type: row.type,
                    target: row.target,
                    status: row.status,
                    isAutomatic: row.isAutomatic,
                    codes: row.codes,
                    percentage: row.percentage,
                    amount: row.amount,
                    maximumDiscountAmount: row.maximumDiscountAmount,
                    allocationMethod: 'Across',
                    appliesTo: row.appliesTo,
                    entitledIds: row.entitledIds,
                    excludedIds: row.excludedIds,
                    customerSelection: row.customerSelection,
                    customerIds: [],
                    customerGroupIds: [],
                    minimumSubtotal: row.minimumSubtotal,
                    minimumQuantity: row.minimumQuantity,
                    buyQuantity: row.buyQuantity,
                    getQuantity: row.getQuantity,
                    getDiscountPercentage: row.getDiscountPercentage,
                    getEntitledIds: [],
                    usageLimit: row.usageLimit,
                    usageLimitPerCustomer: row.usageLimitPerCustomer,
                    startsAt: row.startsAt,
                    endsAt: row.endsAt,
                    combination: row.combination,
                    priority: row.priority,
                    entitledShippingZoneIds: [],
                  },
                })
              }
            >
              Düzenle
            </button>
            <button
              type="button"
              className="btn-ghost btn-sm text-red-600"
              onClick={() => setDeleteId(row.id)}
            >
              Sil
            </button>
          </div>
        ) : null,
    },
  ];

  return (
    <div>
      <PageHeader
        title="İndirimler"
        description="Kod tabanlı ve otomatik kampanyalar"
        actions={
          can(P.discounts.manage) ? (
            <button
              type="button"
              className="btn-primary"
              onClick={() => setEditing({ id: null, form: emptyDiscount() })}
            >
              Yeni indirim
            </button>
          ) : null
        }
      />

      <Toolbar>
        <SearchInput
          value={query.search ?? ''}
          onChange={(value) => setQuery((c) => ({ ...c, search: value || undefined, page: 1 }))}
          placeholder="İndirim adı veya kodu"
        />

        <SelectFilter
          label="Durum"
          value={query.status ?? ''}
          onChange={(value) =>
            setQuery((c) => ({ ...c, status: (value || undefined) as DiscountStatus, page: 1 }))
          }
          options={discountStatusValues.map((value) => ({
            value,
            label: discountStatusLabels[value],
          }))}
        />

        <SelectFilter
          label="Tür"
          value={query.type ?? ''}
          onChange={(value) =>
            setQuery((c) => ({ ...c, type: (value || undefined) as DiscountType, page: 1 }))
          }
          options={discountTypeValues.map((value) => ({
            value,
            label: discountTypeLabels[value],
          }))}
        />

        <label className="mb-1 flex items-center gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-ink-300 text-brand-600"
            checked={query.activeNow === true}
            onChange={(event) =>
              setQuery((c) => ({ ...c, activeNow: event.target.checked || undefined, page: 1 }))
            }
          />
          Şu an geçerli
        </label>
      </Toolbar>

      <DataTable
        columns={columns}
        rows={discounts.data?.items ?? []}
        rowKey={(row) => row.id}
        isLoading={discounts.isLoading}
        error={discounts.error}
        onRetry={() => void discounts.refetch()}
        emptyMessage="İndirim bulunamadı."
        footer={
          discounts.data ? (
            <Pagination
              page={discounts.data.page}
              totalPages={discounts.data.totalPages}
              totalCount={discounts.data.totalCount}
              onChange={(page) => setQuery((c) => ({ ...c, page }))}
            />
          ) : null
        }
      />

      <Modal
        open={editing !== null}
        title={editing?.id === null ? 'Yeni indirim' : 'İndirimi düzenle'}
        onClose={() => setEditing(null)}
        size="lg"
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => save.mutate()}
              disabled={save.isPending || !editing?.form.name}
            >
              {save.isPending ? <Spinner className="h-4 w-4" /> : null}
              Kaydet
            </button>
          </>
        }
      >
        {editing ? (
          <div className="space-y-4">
            <FormErrors error={save.error} />

            <Field label="Ad" htmlFor="dc-name" required>
              <input
                id="dc-name"
                className="field"
                value={editing.form.name}
                onChange={(event) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, name: event.target.value } })
                }
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="İndirim türü" htmlFor="dc-type" required>
                <select
                  id="dc-type"
                  className="field"
                  value={editing.form.type}
                  onChange={(event) =>
                    setEditing(
                      (c) =>
                        c && { ...c, form: { ...c.form, type: event.target.value as DiscountType } },
                    )
                  }
                >
                  {discountTypeValues.map((value) => (
                    <option key={value} value={value}>
                      {discountTypeLabels[value]}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Hedef" htmlFor="dc-target" required>
                <select
                  id="dc-target"
                  className="field"
                  value={editing.form.target}
                  onChange={(event) =>
                    setEditing(
                      (c) =>
                        c && {
                          ...c,
                          form: { ...c.form, target: event.target.value as DiscountUpsertDto['target'] },
                        },
                    )
                  }
                >
                  {discountTargetValues.map((value) => (
                    <option key={value} value={value}>
                      {discountTargetLabels[value]}
                    </option>
                  ))}
                </select>
              </Field>

              {editing.form.type === 'Percentage' ? (
                <Field label="Yüzde" htmlFor="dc-pct" required>
                  <input
                    id="dc-pct"
                    type="number"
                    min={0}
                    max={100}
                    className="field"
                    value={editing.form.percentage ?? 0}
                    onChange={(event) =>
                      setEditing(
                        (c) =>
                          c && { ...c, form: { ...c.form, percentage: Number(event.target.value) } },
                      )
                    }
                  />
                </Field>
              ) : editing.form.type === 'FixedAmount' ? (
                <Field label="Tutar" htmlFor="dc-amount" required>
                  <input
                    id="dc-amount"
                    type="number"
                    step="0.01"
                    min={0}
                    className="field"
                    value={editing.form.amount ?? 0}
                    onChange={(event) =>
                      setEditing(
                        (c) => c && { ...c, form: { ...c.form, amount: Number(event.target.value) } },
                      )
                    }
                  />
                </Field>
              ) : null}

              <Field label="En fazla indirim" htmlFor="dc-max" hint="Yüzde indirimlerde tavan.">
                <input
                  id="dc-max"
                  type="number"
                  step="0.01"
                  min={0}
                  className="field"
                  value={editing.form.maximumDiscountAmount ?? ''}
                  onChange={(event) =>
                    setEditing(
                      (c) =>
                        c && {
                          ...c,
                          form: {
                            ...c.form,
                            maximumDiscountAmount: event.target.value
                              ? Number(event.target.value)
                              : null,
                          },
                        },
                    )
                  }
                />
              </Field>
            </div>

            {editing.form.type === 'BuyXGetY' ? (
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Al (adet)" htmlFor="dc-buy">
                  <input
                    id="dc-buy"
                    type="number"
                    min={1}
                    className="field"
                    value={editing.form.buyQuantity ?? 2}
                    onChange={(event) =>
                      setEditing(
                        (c) =>
                          c && { ...c, form: { ...c.form, buyQuantity: Number(event.target.value) } },
                      )
                    }
                  />
                </Field>
                <Field label="Öde (adet)" htmlFor="dc-get">
                  <input
                    id="dc-get"
                    type="number"
                    min={1}
                    className="field"
                    value={editing.form.getQuantity ?? 1}
                    onChange={(event) =>
                      setEditing(
                        (c) =>
                          c && { ...c, form: { ...c.form, getQuantity: Number(event.target.value) } },
                      )
                    }
                  />
                </Field>
                <Field label="Bedava üründeki indirim %" htmlFor="dc-getpct">
                  <input
                    id="dc-getpct"
                    type="number"
                    min={0}
                    max={100}
                    className="field"
                    value={editing.form.getDiscountPercentage ?? 100}
                    onChange={(event) =>
                      setEditing(
                        (c) =>
                          c && {
                            ...c,
                            form: { ...c.form, getDiscountPercentage: Number(event.target.value) },
                          },
                      )
                    }
                  />
                </Field>
              </div>
            ) : null}

            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-ink-300 text-brand-600"
                checked={editing.form.isAutomatic}
                onChange={(event) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, isAutomatic: event.target.checked } })
                }
              />
              Otomatik indirim (kod gerekmez)
            </label>

            {!editing.form.isAutomatic ? (
              <Field label="Kuponlar" htmlFor="dc-codes" hint="Virgülle ayırın.">
                <input
                  id="dc-codes"
                  className="field font-mono text-sm uppercase"
                  value={editing.form.codes.join(', ')}
                  onChange={(event) =>
                    setEditing(
                      (c) =>
                        c && {
                          ...c,
                          form: {
                            ...c.form,
                            codes: event.target.value
                              .split(',')
                              .map((code) => code.trim().toUpperCase())
                              .filter(Boolean),
                          },
                        },
                    )
                  }
                />
              </Field>
            ) : null}

            <div className="space-y-4 rounded-lg border border-ink-200 bg-white p-3.5">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Kapsam" htmlFor="dc-applies">
                  <select
                    id="dc-applies"
                    className="field"
                    value={editing.form.appliesTo}
                    onChange={(event) => {
                      const newApplies = event.target.value as DiscountUpsertDto['appliesTo'];
                      setEditing(
                        (c) =>
                          c && {
                            ...c,
                            form: {
                              ...c.form,
                              appliesTo: newApplies,
                              entitledIds: newApplies === 'All' ? [] : c.form.entitledIds,
                            },
                          },
                      );
                    }}
                  >
                    {discountSelectionValues.map((value) => (
                      <option key={value} value={value}>
                        {discountSelectionLabels[value]}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Müşteri seçimi" htmlFor="dc-customers">
                  <select
                    id="dc-customers"
                    className="field"
                    value={editing.form.customerSelection}
                    onChange={(event) =>
                      setEditing(
                        (c) =>
                          c && {
                            ...c,
                            form: {
                              ...c.form,
                              customerSelection: event.target
                                .value as DiscountUpsertDto['customerSelection'],
                            },
                          },
                      )
                    }
                  >
                    {discountCustomerSelectionValues.map((value) => (
                      <option key={value} value={value}>
                        {discountCustomerSelectionLabels[value]}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              {/* Kategori Bazlı Kupon Seçici */}
              {editing.form.appliesTo === 'EntitledCategories' ? (
                <CategoryPicker
                  selectedIds={editing.form.entitledIds}
                  onChange={(ids) =>
                    setEditing((c) => c && { ...c, form: { ...c.form, entitledIds: ids } })
                  }
                />
              ) : null}

              {/* Ürün Bazlı Kupon Seçici */}
              {editing.form.appliesTo === 'EntitledProducts' ? (
                <ProductPicker
                  selectedIds={editing.form.entitledIds}
                  onChange={(ids) =>
                    setEditing((c) => c && { ...c, form: { ...c.form, entitledIds: ids } })
                  }
                />
              ) : null}

              {/* Elle ID Girişi (Gelişmiş) */}
              {editing.form.appliesTo !== 'All' ? (
                <details className="text-xs text-ink-500">
                  <summary className="cursor-pointer font-medium hover:text-ink-800">
                    Gelişmiş: Kapsam ID listesi ({editing.form.entitledIds.length} adet)
                  </summary>
                  <div className="mt-2">
                    <input
                      id="dc-entitled"
                      className="field font-mono text-xs"
                      placeholder="Virgülle ID girin (örn: 10, 24, 35)"
                      value={editing.form.entitledIds.join(', ')}
                      onChange={(event) =>
                        setEditing(
                          (c) =>
                            c && {
                              ...c,
                              form: {
                                ...c.form,
                                entitledIds: event.target.value
                                  .split(',')
                                  .map((raw) => Number(raw.trim()))
                                  .filter((value) => Number.isFinite(value) && value > 0),
                              },
                            },
                        )
                      }
                    />
                  </div>
                </details>
              ) : null}

              {/* İlk Sipariş Kuponu Bilgilendirme Notu */}
              {editing.form.customerSelection === 'FirstTimeBuyers' ? (
                <div className="rounded-lg border border-brand-200 bg-brand-50 p-2.5 text-xs text-brand-900 flex items-start gap-2">
                  <span className="text-base leading-none">🎉</span>
                  <div>
                    <strong>İlk Sipariş Kuponu:</strong> Bu indirim kuralı yalnızca mağazanızdan ilk kez alışveriş yapacak müşteriler için geçerlidir. Giriş yapmış üyelerin geçmiş sipariş geçmişi ve misafir siparişte girilen e-posta adresi otomatik denetlenir.
                  </div>
                </div>
              ) : null}

              {/* Belirli Müşteriler Seçici */}
              {editing.form.customerSelection === 'SpecificCustomers' ? (
                <CustomerPicker
                  selectedIds={editing.form.customerIds}
                  onChange={(ids) =>
                    setEditing((c) => c && { ...c, form: { ...c.form, customerIds: ids } })
                  }
                />
              ) : null}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Dağıtım yöntemi" htmlFor="dc-alloc">
                <select
                  id="dc-alloc"
                  className="field"
                  value={editing.form.allocationMethod}
                  onChange={(event) =>
                    setEditing(
                      (c) =>
                        c && {
                          ...c,
                          form: {
                            ...c.form,
                            allocationMethod: event.target
                              .value as DiscountUpsertDto['allocationMethod'],
                          },
                        },
                    )
                  }
                >
                  {discountAllocationMethodValues.map((value) => (
                    <option key={value} value={value}>
                      {value === 'Across' ? 'Toplama dağıt' : 'Her kaleme ayrı'}
                    </option>
                  ))}
                </select>
              </Field>

              <Field
                label="En az sepet tutarı"
                htmlFor="dc-minsub"
                hint="İsteğe bağlı. Boş bırakılırsa sepet alt limiti aranmaz."
              >
                <input
                  id="dc-minsub"
                  type="number"
                  step="0.01"
                  min={0}
                  className="field"
                  value={editing.form.minimumSubtotal ?? ''}
                  onChange={(event) =>
                    setEditing(
                      (c) =>
                        c && {
                          ...c,
                          form: {
                            ...c.form,
                            minimumSubtotal: event.target.value ? Number(event.target.value) : null,
                          },
                        },
                    )
                  }
                />
              </Field>

              <Field label="En az adet" htmlFor="dc-minqty">
                <input
                  id="dc-minqty"
                  type="number"
                  min={0}
                  className="field"
                  value={editing.form.minimumQuantity ?? ''}
                  onChange={(event) =>
                    setEditing(
                      (c) =>
                        c && {
                          ...c,
                          form: {
                            ...c.form,
                            minimumQuantity: event.target.value ? Number(event.target.value) : null,
                          },
                        },
                    )
                  }
                />
              </Field>

              <Field label="Toplam kullanım sınırı" htmlFor="dc-limit">
                <input
                  id="dc-limit"
                  type="number"
                  min={0}
                  className="field"
                  value={editing.form.usageLimit ?? ''}
                  onChange={(event) =>
                    setEditing(
                      (c) =>
                        c && {
                          ...c,
                          form: {
                            ...c.form,
                            usageLimit: event.target.value ? Number(event.target.value) : null,
                          },
                        },
                    )
                  }
                />
              </Field>

              <Field label="Müşteri başına sınır" htmlFor="dc-limitper">
                <input
                  id="dc-limitper"
                  type="number"
                  min={0}
                  className="field"
                  value={editing.form.usageLimitPerCustomer ?? ''}
                  onChange={(event) =>
                    setEditing(
                      (c) =>
                        c && {
                          ...c,
                          form: {
                            ...c.form,
                            usageLimitPerCustomer: event.target.value
                              ? Number(event.target.value)
                              : null,
                          },
                        },
                    )
                  }
                />
              </Field>

              <Field label="Başlangıç" htmlFor="dc-start" required>
                <input
                  id="dc-start"
                  type="date"
                  className="field"
                  value={editing.form.startsAt.slice(0, 10)}
                  onChange={(event) =>
                    setEditing(
                      (c) =>
                        c && {
                          ...c,
                          form: {
                            ...c.form,
                            startsAt: new Date(`${event.target.value}T00:00:00.000Z`).toISOString(),
                          },
                        },
                    )
                  }
                />
              </Field>

              <Field label="Bitiş" htmlFor="dc-end" hint="Boşsa süresizdir.">
                <input
                  id="dc-end"
                  type="date"
                  className="field"
                  value={editing.form.endsAt?.slice(0, 10) ?? ''}
                  onChange={(event) =>
                    setEditing(
                      (c) =>
                        c && {
                          ...c,
                          form: {
                            ...c.form,
                            endsAt: event.target.value
                              ? new Date(`${event.target.value}T23:59:59.999Z`).toISOString()
                              : null,
                          },
                        },
                    )
                  }
                />
              </Field>

              <Field label="Durum" htmlFor="dc-status">
                <select
                  id="dc-status"
                  className="field"
                  value={editing.form.status}
                  onChange={(event) =>
                    setEditing(
                      (c) =>
                        c && {
                          ...c,
                          form: { ...c.form, status: event.target.value as DiscountStatus },
                        },
                    )
                  }
                >
                  {discountStatusValues.map((value) => (
                    <option key={value} value={value}>
                      {discountStatusLabels[value]}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Diğer indirimlerle birleşim" htmlFor="dc-combo">
                <select
                  id="dc-combo"
                  className="field"
                  value={editing.form.combination}
                  onChange={(event) =>
                    setEditing(
                      (c) =>
                        c && {
                          ...c,
                          form: {
                            ...c.form,
                            combination: event.target.value as DiscountUpsertDto['combination'],
                          },
                        },
                    )
                  }
                >
                  {discountCombinationValues.map((value) => (
                    <option key={value} value={value}>
                      {discountCombinationLabels[value]}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={generateFor !== null}
        title="Kupon kodu üret"
        description="Üretilen kodlar metin dosyası olarak indirilir."
        onClose={() => setGenerateFor(null)}
        size="sm"
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setGenerateFor(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => generate.mutate()}
              disabled={generate.isPending}
            >
              {generate.isPending ? <Spinner className="h-4 w-4" /> : null}
              Üret
            </button>
          </>
        }
      >
        <Field label="Kod adedi" htmlFor="dc-count" required>
          <input
            id="dc-count"
            type="number"
            min={1}
            max={10000}
            className="field"
            value={generateCount}
            onChange={(event) => setGenerateCount(Number(event.target.value))}
          />
        </Field>
      </Modal>

      <ConfirmDialog
        open={deleteId !== null}
        title="İndirimi sil"
        message="Kullanılmış indirimler silinemeyebilir; bunun yerine kapatmayı deneyin."
        confirmLabel="Sil"
        danger
        busy={remove.isPending}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => deleteId !== null && remove.mutate(deleteId)}
      />
    </div>
  );
}

/* ============================== HEDIYE KARTLARI ============================== */

const giftCardStatusLabels: Record<GiftCardStatus, string> = {
  Active: 'Aktif',
  PartiallyRedeemed: 'Kısmen kullanıldı',
  Redeemed: 'Bakiyesi bitti',
  Expired: 'Süresi doldu',
  Disabled: 'Kapatıldı',
};

export function GiftCardsPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [query, setQuery] = useState<GiftCardQueryDto>({ page: 1, pageSize: 20 });
  const [createOpen, setCreateOpen] = useState(false);
  const [adjusting, setAdjusting] = useState<{ id: number; amount: number; note: string } | null>(
    null,
  );
  const [disableId, setDisableId] = useState<number | null>(null);
  const [issuedCode, setIssuedCode] = useState<string | null>(null);

  const [form, setForm] = useState<GiftCardCreateDto>({
    amount: 500,
    currencyCode: 'TRY',
    sendEmail: true,
  });

  const cards = useQuery({
    queryKey: ['admin', 'gift-cards', query],
    queryFn: () => adminGiftCardsApi.search(query),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'gift-cards'] });

  const create = useMutation({
    mutationFn: () => adminGiftCardsApi.create(form),
    onSuccess: (card) => {
      // Kod yalnizca bu yanitta doner; kaybolursa bir daha gorulemez.
      setIssuedCode(card.plainCode ?? null);
      setCreateOpen(false);
      void invalidate();
    },
  });

  const adjust = useMutation({
    mutationFn: () =>
      adminGiftCardsApi.adjust(adjusting!.id, {
        amount: adjusting!.amount,
        note: adjusting!.note || null,
      }),
    onSuccess: () => {
      toast.success('Bakiye güncellendi.');
      setAdjusting(null);
      void invalidate();
    },
  });

  const disable = useMutation({
    mutationFn: (id: number) => adminGiftCardsApi.disable(id),
    onSuccess: (message) => {
      toast.success(message ?? 'Hediye kartı kapatıldı.');
      setDisableId(null);
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'İşlem tamamlanamadı.'),
  });

  const columns: Column<NonNullable<typeof cards.data>['items'][number]>[] = [
    {
      key: 'code',
      header: 'Kod',
      cell: (row) => <span className="font-mono text-sm text-ink-800">{row.maskedCode}</span>,
    },
    {
      key: 'balance',
      header: 'Bakiye',
      align: 'right',
      cell: (row) => (
        <span className="font-medium text-ink-900">{money(row.balance, row.currencyCode)}</span>
      ),
    },
    {
      key: 'initial',
      header: 'Yüklenen',
      align: 'right',
      hideOnMobile: true,
      cell: (row) => money(row.initialValue, row.currencyCode),
    },
    {
      key: 'recipient',
      header: 'Alıcı',
      hideOnMobile: true,
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate text-ink-800">{row.recipientName ?? '—'}</p>
          <p className="truncate text-xs text-ink-400">{row.recipientEmail ?? ''}</p>
        </div>
      ),
    },
    {
      key: 'expires',
      header: 'Son kullanım',
      hideOnMobile: true,
      cell: (row) => <span className="text-ink-500">{formatDate(row.expiresAt)}</span>,
    },
    {
      key: 'status',
      header: 'Durum',
      cell: (row) => (
        <span
          className={`badge ${
            row.status === 'Active'
              ? 'bg-emerald-50 text-emerald-700'
              : row.status === 'PartiallyRedeemed'
                ? 'bg-brand-50 text-brand-800'
                : 'bg-ink-100 text-ink-600'
          }`}
        >
          {giftCardStatusLabels[row.status]}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (row) =>
        can(P.discounts.manage) ? (
          <div className="flex justify-end gap-1">
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={() => setAdjusting({ id: row.id, amount: 0, note: '' })}
            >
              Bakiye
            </button>
            {row.status !== 'Disabled' ? (
              <button
                type="button"
                className="btn-ghost btn-sm text-red-600"
                onClick={() => setDisableId(row.id)}
              >
                Kapat
              </button>
            ) : null}
          </div>
        ) : null,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Hediye kartları"
        description="Hediye kartı bir ödeme yöntemidir; KDV matrahını düşürmez."
        actions={
          can(P.discounts.manage) ? (
            <button type="button" className="btn-primary" onClick={() => setCreateOpen(true)}>
              Yeni hediye kartı
            </button>
          ) : null
        }
      />

      <Toolbar>
        <SelectFilter
          label="Durum"
          value={query.status ?? ''}
          onChange={(value) =>
            setQuery((c) => ({ ...c, status: (value || undefined) as GiftCardStatus, page: 1 }))
          }
          options={giftCardStatusValues.map((value) => ({
            value,
            label: giftCardStatusLabels[value],
          }))}
        />

        <label className="mb-1 flex items-center gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-ink-300 text-brand-600"
            checked={query.hasBalance === true}
            onChange={(event) =>
              setQuery((c) => ({ ...c, hasBalance: event.target.checked || undefined, page: 1 }))
            }
          />
          Yalnızca bakiyesi olanlar
        </label>
      </Toolbar>

      <DataTable
        columns={columns}
        rows={cards.data?.items ?? []}
        rowKey={(row) => row.id}
        isLoading={cards.isLoading}
        error={cards.error}
        onRetry={() => void cards.refetch()}
        emptyMessage="Hediye kartı yok."
        footer={
          cards.data ? (
            <Pagination
              page={cards.data.page}
              totalPages={cards.data.totalPages}
              totalCount={cards.data.totalCount}
              onChange={(page) => setQuery((c) => ({ ...c, page }))}
            />
          ) : null
        }
      />

      <Modal
        open={createOpen}
        title="Yeni hediye kartı"
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setCreateOpen(false)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => create.mutate()}
              disabled={create.isPending || form.amount <= 0}
            >
              {create.isPending ? <Spinner className="h-4 w-4" /> : null}
              Oluştur
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <FormErrors error={create.error} />

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Tutar" htmlFor="gc-amount" required>
              <input
                id="gc-amount"
                type="number"
                step="0.01"
                min={1}
                className="field"
                value={form.amount}
                onChange={(event) => setForm((c) => ({ ...c, amount: Number(event.target.value) }))}
              />
            </Field>

            <Field label="Son kullanım" htmlFor="gc-expires">
              <input
                id="gc-expires"
                type="date"
                className="field"
                value={form.expiresAt?.slice(0, 10) ?? ''}
                onChange={(event) =>
                  setForm((c) => ({
                    ...c,
                    expiresAt: event.target.value ? new Date(event.target.value).toISOString() : null,
                  }))
                }
              />
            </Field>

            <Field label="Alıcı adı" htmlFor="gc-name">
              <input
                id="gc-name"
                className="field"
                value={form.recipientName ?? ''}
                onChange={(event) => setForm((c) => ({ ...c, recipientName: event.target.value }))}
              />
            </Field>

            <Field label="Alıcı e-postası" htmlFor="gc-email">
              <input
                id="gc-email"
                type="email"
                className="field"
                value={form.recipientEmail ?? ''}
                onChange={(event) => setForm((c) => ({ ...c, recipientEmail: event.target.value }))}
              />
            </Field>
          </div>

          <Field label="Kişisel mesaj" htmlFor="gc-message">
            <textarea
              id="gc-message"
              className="field min-h-20"
              value={form.personalMessage ?? ''}
              onChange={(event) => setForm((c) => ({ ...c, personalMessage: event.target.value }))}
            />
          </Field>

          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-ink-300 text-brand-600"
              checked={form.sendEmail}
              onChange={(event) => setForm((c) => ({ ...c, sendEmail: event.target.checked }))}
            />
            Alıcıya e-posta gönder
          </label>
        </div>
      </Modal>

      <Modal
        open={issuedCode !== null}
        title="Hediye kartı oluşturuldu"
        description="Bu kod yalnızca bir kez gösterilir; kaydetmeyi unutmayın."
        onClose={() => setIssuedCode(null)}
        size="sm"
        footer={
          <button type="button" className="btn-primary" onClick={() => setIssuedCode(null)}>
            Kapat
          </button>
        }
      >
        <p className="rounded-lg bg-ink-900 px-4 py-3 text-center font-mono text-lg tracking-wider text-white">
          {issuedCode}
        </p>
      </Modal>

      <Modal
        open={adjusting !== null}
        title="Bakiye düzeltmesi"
        description="Artış için pozitif, azalış için negatif tutar girin."
        onClose={() => setAdjusting(null)}
        size="sm"
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setAdjusting(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => adjust.mutate()}
              disabled={adjust.isPending || !adjusting?.amount}
            >
              {adjust.isPending ? <Spinner className="h-4 w-4" /> : null}
              Uygula
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <FormErrors error={adjust.error} />

          <Field label="Tutar" htmlFor="gc-adjust" required>
            <input
              id="gc-adjust"
              type="number"
              step="0.01"
              className="field"
              value={adjusting?.amount ?? 0}
              onChange={(event) =>
                setAdjusting((c) => c && { ...c, amount: Number(event.target.value) })
              }
            />
          </Field>

          <Field label="Not" htmlFor="gc-adjust-note">
            <input
              id="gc-adjust-note"
              className="field"
              value={adjusting?.note ?? ''}
              onChange={(event) => setAdjusting((c) => c && { ...c, note: event.target.value })}
            />
          </Field>
        </div>
      </Modal>

      <ConfirmDialog
        open={disableId !== null}
        title="Hediye kartını kapat"
        message="Kart bir daha kullanılamaz. Bakiye iade edilmez."
        confirmLabel="Kapat"
        danger
        busy={disable.isPending}
        onCancel={() => setDisableId(null)}
        onConfirm={() => disableId !== null && disable.mutate(disableId)}
      />
    </div>
  );
}
